import type {
  MemberMeetingAgendaResponse,
  MemberMeetingGetAllResponse,
  MemberMeetingGetResponse,
  MemberMeetingMinutesEntryDetailsResponse,
  MemberMeetingMinutesEntryResponse,
  MemberMeetingMinutesVoteResponse,
  MemberMeetingOpenVoteResponse,
  MemberMeetingSpeakerQueuePositionResponse,
  MemberMeetingsSearchInput,
  MemberMeetingsSearchResponse,
} from '@govinity/validation/public';
import type { MemberVoteType } from '@govinity/validation';

import { ReadOnlyApi } from '../base';

const PATH = '/members/meetings';

/**
 * API class for member meetings endpoints.
 * These endpoints require the user to have an active seat assignment.
 */
class MembersMeetingsApi extends ReadOnlyApi<
  MemberMeetingGetAllResponse,
  MemberMeetingGetResponse,
  MemberMeetingsSearchInput,
  MemberMeetingsSearchResponse
> {
  getPath() {
    return PATH;
  }

  async getAgenda(meetingId: number) {
    const { data } = await this.request<MemberMeetingAgendaResponse>({
      method: 'GET',
      url: `${this.getPath()}/${meetingId}/agenda`,
    });

    return data;
  }

  async getMinutesEntries(meetingId: number) {
    const { data } = await this.request<MemberMeetingMinutesEntryResponse[]>({
      method: 'GET',
      url: `${this.getPath()}/${meetingId}/minutes-entries`,
    });

    return data;
  }

  async getMinutesEntry(meetingId: number, entryId: number) {
    const { data } = await this.request<MemberMeetingMinutesEntryDetailsResponse>({
      method: 'GET',
      url: `${this.getPath()}/${meetingId}/minutes-entries/${entryId}`,
    });

    return data;
  }

  async getOpenVote(meetingId: number) {
    const { data } = await this.request<MemberMeetingOpenVoteResponse>({
      method: 'GET',
      url: `${this.getPath()}/${meetingId}/open-vote`,
    });

    return data;
  }

  async castMinutesEntryMotionVote(
    meetingId: number,
    entryId: number,
    motionId: number,
    vote: MemberVoteType
  ) {
    const { data } = await this.request<MemberMeetingMinutesVoteResponse>({
      method: 'PUT',
      url: `${this.getPath()}/${meetingId}/minutes-entries/${entryId}/motions/${motionId}/vote`,
      data: { vote },
    });

    return data;
  }

  /**
   * Reads the member's standing in an entry's speaker queue. Returns null-valued own-request fields
   * when the member has no active request, so it doubles as the "have I signed up?" check.
   */
  async getSpeakerQueuePosition(meetingId: number, entryId: number) {
    const { data } = await this.request<MemberMeetingSpeakerQueuePositionResponse>({
      method: 'GET',
      url: `${this.getPath()}/${meetingId}/speakers/${entryId}/position`,
    });

    return data;
  }

  /**
   * Adds the member to the speaker queue for an entry. The API only accepts this while the meeting
   * is live and the entry is the one on the floor, and it is idempotent for an existing request.
   */
  async createSpeakerRequest(meetingId: number, entryId: number) {
    const { data } = await this.request<MemberMeetingSpeakerQueuePositionResponse>({
      method: 'POST',
      url: `${this.getPath()}/${meetingId}/speakers/${entryId}`,
    });

    return data;
  }

  /** Withdraws the member's queued speaker request for an entry. Succeeds even if nothing is queued. */
  async deleteSpeakerRequest(meetingId: number, entryId: number) {
    await this.request({
      method: 'DELETE',
      url: `${this.getPath()}/${meetingId}/speakers/${entryId}`,
    });
  }
}

export default MembersMeetingsApi;
